import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

/**
 * Online Courses Header Component
 * Displays course information and CTA
 */

// Fallback data
const DEFAULT_ONLINE_COURSES_DATA = {
  courseCount: '10+',
  title: 'Online Classes',
  titleHighlight: 'Available Now',
  description: 'Learn from home with our',
  descriptionHighlight: 'comprehensive IELTS courses',
  buttonText: 'Explore Courses',
  courseLink: '/courses',
};

interface OnlineCoursesHeaderProps {
  data?: typeof DEFAULT_ONLINE_COURSES_DATA;
}

const OnlineCoursesHeader: React.FC<OnlineCoursesHeaderProps> = ({ data }) => {
  const navigate = useNavigate();

  // Use fallback if data is not provided
  const headerData = data || DEFAULT_ONLINE_COURSES_DATA;
  return (
    <motion.div
      className="relative bg-transparent my-4 sm:my-6 md:my-8 w-full max-w-md overflow-visible"
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
    >
      {/* Floating badge with number of courses - 50% overflow outside container */}
      <motion.div
        className="absolute w-14 h-14 md:w-16 md:h-16 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-base md:text-lg shadow-lg border-2 border-white dark:border-gray-800 z-20"
        style={{ top: 0, right: 0 }}
        initial={{ scale: 0, opacity: 0, x: '30%', y: '-50%' }}
        whileInView={{ scale: 1, opacity: 1, x: '30%', y: '-50%' }}
        viewport={{ once: true }}
        transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.5 }}
      >
        {headerData.courseCount}
      </motion.div>

      {/* Main card */}
      <div className="relative bg-gradient-to-br from-gray-50/90 to-gray-100/90 dark:from-gray-900/90 dark:to-gray-800/90 rounded-xl shadow-lg overflow-hidden backdrop-blur-sm">
        {/* Main content */}
        <div className="py-4 px-4 sm:py-6 sm:px-6 md:py-8 md:px-8 flex flex-col md:flex-row items-center gap-4 md:gap-6 relative z-10">
          {/* Text content */}
          <div className="flex-1">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-xl md:text-2xl font-bold text-gray-800 dark:text-white mb-2">
                <span className="text-red-600 dark:text-red-500">{headerData.title}</span> {headerData.titleHighlight}
              </h2>

              <p className="text-gray-600 dark:text-gray-300 text-sm md:text-base mb-3 leading-relaxed">
                {headerData.description}
                <span className="text-green-600 dark:text-green-500 font-medium"> {headerData.descriptionHighlight}</span>
              </p>

              <motion.button
                onClick={() => navigate(headerData.courseLink)}
                whileHover={{
                  scale: 1.05,
                  boxShadow: '0 10px 15px -3px rgba(20, 184, 166, 0.2), 0 4px 6px -4px rgba(20, 184, 166, 0.2)',
                }}
                whileTap={{ scale: 0.98 }}
                className="px-6 py-3 bg-gradient-to-r from-teal-500 to-emerald-400 hover:from-teal-600 hover:to-emerald-500 text-white font-medium rounded-lg shadow-md transition-all duration-300 flex items-center space-x-2"
              >
                <i className="fas fa-play-circle"></i>
                <span>{headerData.buttonText}</span>
              </motion.button>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default OnlineCoursesHeader;
